import { Close as DiscardIcon } from '@mui/icons-material';
import { Chip, Grid, IconButton, MenuItem, Paper } from '@mui/material';
import { Theme } from '@mui/material/styles';
import AutosuggestHighlightMatch from 'autosuggest-highlight/match';
import AutosuggestHighlightParse from 'autosuggest-highlight/parse';
import * as i18next from 'i18next';
import { findIndex } from 'lodash';
import React from 'react';
import Autosuggest, {
  InputProps,
  RenderSuggestionsContainerParams,
  SuggestionSelectedEventData,
} from 'react-autosuggest';
import { compose } from 'recompose';

import ChipInput, { ChipRendererArgs } from '@/components/chip-filter/chip-input';
import { WithStyles, withStyles } from '@/hocs/with-styles';
import * as Types from '@/types';

const styles = (theme: Theme) => ({
  container: {
    flexGrow: 0,
    position: 'relative',
  },
  suggestionsContainerOpen: {
    position: 'absolute',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    maxHeight: 300,
    maxWidth: 400,
    overflowY: 'auto',
    left: 0,
    zIndex: 100,
    right: 0,
  },
  suggestion: {
    display: 'block',
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  textField: {
    width: '100%',
  },
  discardGrid: {
    direction: 'rtl',
  },
  discardButton: {
    padding: 8,
    '@media print': {
      display: 'none',
    },
  },
});

interface Chip {
  key: string;
  value: string;
}

interface RenderInputProps extends InputProps<Types.Suggestion> {
  value: string;
  onAdd: (chip: Chip) => void;
  onDelete: (chip: unknown, index: number) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  chips: Chip[];
  ref: (ref: unknown) => void;
}

interface Properties {
  applyFilter: (filterObj: Chip[]) => void;
  suggestions: Types.Suggestion[];
  t: i18next.TFunction;
  filters?: Chip[];
  placeholderText?: string;
}

interface State {
  value: Chip[];
  textFieldInput: string;
  suggestions: Types.Suggestion[];
}

class AutosuggestChipInput extends React.Component<Properties & WithStyles<typeof styles>, State> {
  static getDerivedStateFromProps(
    { filters = [], suggestions = [] }: Readonly<Properties>,
    { value = [], ...state }: State,
  ) {
    return {
      ...state,
      value: filters.concat(value).filter((item, idx, arr) => {
        if (
          item.key.toUpperCase() !== 'SEARCH' &&
          !suggestions.some((suggestion) => item.key.toUpperCase() === suggestion.key.toUpperCase())
        ) {
          return false;
        }

        return arr.findIndex((i) => i.key === item.key && i.value === item.value) === idx;
      }),
    };
  }
  readonly state: State = {
    value: [],
    suggestions: [],
    textFieldInput: '',
  };
  justClicked: boolean = false;
  currentKey: string | undefined;

  chipRenderer = (
    { chip, isFocused, isDisabled, handleClick, handleDelete, className }: ChipRendererArgs,
    key: React.ReactText,
  ) => {
    let chipKey;
    let chipName;
    if (chip.key === 'search') {
      chipKey = this.props.t(['shared:search'], { defaultValue: 'Search' });
      chipName = chip.value;
    } else if (chip.key === 'name') {
      chipKey = this.props.t(['shared:name'], { defaultValue: 'Name' });
      chipName = chip.value;
    } else {
      const [suggestion] = this.props.suggestions.filter((s) => chip.key.toUpperCase() === s.key.toUpperCase());

      if (!suggestion) {
        return null;
      }

      const [option] = suggestion.options.filter((o) => o.value?.toUpperCase() === chip.value.toUpperCase());

      if (!option) {
        return null;
      }

      chipKey = suggestion.name;
      chipName = option?.name;
    }
    return (
      <Chip
        key={key}
        className={className}
        style={{
          pointerEvents: isDisabled ? 'none' : undefined,
          backgroundColor: isFocused ? this.props.theme.palette.primary.main : undefined,
          cursor: isFocused ? 'text' : 'pointer',
        }}
        onClick={handleClick}
        onDelete={handleDelete}
        label={
          <p>
            <b>{chipKey}</b>: {chipName}
          </p>
        }
      />
    );
  };

  renderInput = ({
    classes,
    autoFocus,
    value,
    onChange,
    onAdd,
    onDelete,
    chips,
    ref,
    ...other
  }: RenderInputProps & WithStyles<typeof styles>): React.ReactNode => {
    return (
      <ChipInput
        allowDuplicates={false}
        clearInputValueOnChange={false}
        newChipKeyCodes={[13]}
        chipRenderer={this.chipRenderer}
        onUpdateInput={onChange}
        onAdd={onAdd}
        onDelete={onDelete}
        inputValue={value}
        value={chips}
        inputRef={ref}
        placeholder={
          this.props.placeholderText
            ? this.props.placeholderText
            : this.props.t(['shared:filterValues'], { defaultValue: 'Filter values..' })
        }
        {...other}
      />
    );
  };

  renderSuggestion = (
    suggestion: Types.Suggestion,
    {
      query,
      isHighlighted,
    }: {
      query: string;
      isHighlighted: boolean;
    },
  ) => {
    if (this.currentKey) {
      query = query.toLowerCase().split(`${this.currentKey.toLowerCase()}:`)[1];
    }
    const matches = AutosuggestHighlightMatch(suggestion.name, query);
    const parts = AutosuggestHighlightParse(suggestion.name, matches);

    return (
      <MenuItem selected={isHighlighted} component="div">
        <div>
          {parts.map((part, index) => {
            return part.highlight ? (
              <span key={String(index)} style={{ color: this.props.theme.palette.primary.main, fontWeight: 500 }}>
                {part.text}
              </span>
            ) : (
              <strong key={String(index)} style={{ fontWeight: 500 }}>
                {part.text}
              </strong>
            );
          })}
        </div>
      </MenuItem>
    );
  };

  renderSuggestionsContainer = ({ containerProps, children }: RenderSuggestionsContainerParams) => {
    return (
      <Paper {...containerProps} square>
        {children}
      </Paper>
    );
  };

  escapeRegexCharacters = (str: string): string | [] => {
    return str ? str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : [];
  };

  getSuggestions = (value: string): Types.Suggestion | Types.Suggestion['options'] => {
    let returnSuggestions: Types.Suggestion | Types.Suggestion['options'] = this.props.suggestions;
    let testValue = value.trim();

    if (this.currentKey) {
      const index = findIndex(this.props.suggestions, (s: Types.Suggestion) => s.name === this.currentKey);
      returnSuggestions = this.props.suggestions[index].options;
      testValue = testValue.toLowerCase().split(`${this.currentKey.toLowerCase()}:`)[1];

      if (testValue === undefined) {
        testValue = '';
      }
    }
    const escapedValue = this.escapeRegexCharacters(testValue.trim());
    if (escapedValue === '') {
      return returnSuggestions;
    }
    return returnSuggestions;
  };

  getSuggestionValue = (suggestion: Types.Suggestion) => {
    return suggestion.name;
  };

  shouldRenderSuggestions = () => {
    return true;
  };

  handleSuggestionsFetchRequested = ({ value }: { value: string }) => {
    this.setState({
      suggestions: this.getSuggestions(value) as Types.Suggestion[],
    });
  };

  handleSuggestionsClearRequested = () => {
    this.setState({
      suggestions: this.props.suggestions,
    });
  };

  handletextFieldInputChange = (
    event: Event,
    {
      newValue,
      method,
    }: {
      newValue: string;
      method: string;
    },
  ) => {
    if (method === 'click' || method === 'enter') {
      event.preventDefault();
      return;
    }

    if ((method === 'up' || method === 'down') && this.currentKey !== newValue.split(':').shift()) {
      if (this.currentKey) {
        newValue = this.currentKey + ': ' + newValue;
      } else {
        newValue = newValue;
      }
    }
    if (!newValue && method === 'type' && this.state.textFieldInput.length > 1) {
      newValue = this.state.textFieldInput;
    }
    if (method === 'type' && newValue) {
      if (!this.justClicked) {
        const matchingSuggestions = this.props.suggestions.filter((suggestion) => {
          const suggestionRegex = new RegExp('^' + this.escapeRegexCharacters(suggestion.name) + ':', 'i');
          return suggestionRegex.test(newValue.trim());
        });
        if (matchingSuggestions.length) {
          this.currentKey = matchingSuggestions[0].name;
          // this.setState({
          //   currentKey: matchingSuggestions[0].name,
          // });
        } else {
          this.currentKey = undefined;
          // this.setState({
          //   currentKey: undefined,
          // });
        }
      } else {
        this.justClicked = false;
        // this.setState({
        //   justClicked: false,
        // });
      }
    }
    if (method === 'type' || method === 'up' || method === 'down') {
      this.setState({
        textFieldInput: newValue,
      });
    }
  };

  handleAddChip = (chip: string) => {
    let key = 'search';
    let value = chip;
    let index: number | undefined;
    if (this.currentKey) {
      // In this case, the textFieldValue contains both a suggestion and a value, seperated by ':'
      index = findIndex(this.props.suggestions, (s: Types.Suggestion) => s.name === this.currentKey);

      key = this.props.suggestions[index].key ? this.props.suggestions[index].key : this.props.suggestions[index].name;
      value = chip.toLowerCase().split(`${this.currentKey.toLowerCase()}:`)[1];
      const validOptions = this.props.suggestions[index].options.map((s) =>
        typeof s.name === 'string' ? s.value?.toLowerCase() : '',
      );
      if (value && !validOptions.includes(value.trim())) {
        this.setState({
          textFieldInput: key + ':',
        });

        return null;
      }
    } else if (chip.startsWith(`${key}:`)) {
      value = chip.toLowerCase().split(`${key}:`)[1];
    }

    if (!value) {
      return null;
    }
    // Here the key and value is guaranteed to be set, where key is either a suggestion name or 'search'
    let stateValue = this.state.value;
    if (this.props.suggestions[index ?? -1]?.justOne) {
      stateValue = stateValue.filter((item) => item.key !== key);
    }
    stateValue = [...stateValue, { key, value: value.trim() }];
    this.setState({
      value: stateValue,
      textFieldInput: '',
    });
    this.handleSuggestionsFetchRequested({ value: '' });

    if (this.props.applyFilter) {
      this.props.applyFilter(stateValue);
    }
    this.currentKey = undefined;
    return true;
  };

  handleOnInputEnter = (event: Event) => {
    if ((event.target as HTMLInputElement).value.trim().length) {
      const result = this.handleAddChip((event.target as HTMLInputElement).value);
      if (result) {
        this.currentKey = undefined;
        this.handleSuggestionsClearRequested();
        this.handleSuggestionsFetchRequested({ value: '' });
      }
    }
  };

  handleDeleteChip(_: unknown, index: number) {
    // chips.splice(index, 1);
    const newChips = this.state.value.filter((__, i) => i !== index);

    this.setState({ value: newChips });

    if (this.props.applyFilter) {
      this.props.applyFilter(newChips);
    }
  }
  clear = () => {
    this.setState({ value: [], textFieldInput: '' }, () => {
      if (!this.props.applyFilter) {
        return;
      }

      delete this.currentKey;
      this.props.applyFilter([]);
    });
  };

  public render() {
    const { classes } = this.props;

    // FIXME: Autosuggest does not infer inputProps to renderInputComponent,
    // hence the cast.
    return (
      <Grid container>
        <Grid item xs={11}>
          <Autosuggest
            theme={{
              container: classes.container,
              suggestionsContainerOpen: classes.suggestionsContainerOpen,
              suggestionsList: classes.suggestionsList,
              suggestion: classes.suggestion,
            }}
            renderInputComponent={this.renderInput as any}
            suggestions={this.state.suggestions}
            onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
            onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
            renderSuggestionsContainer={this.renderSuggestionsContainer}
            getSuggestionValue={this.getSuggestionValue}
            shouldRenderSuggestions={this.shouldRenderSuggestions}
            renderSuggestion={this.renderSuggestion}
            onSuggestionSelected={this.onSuggestionSelected}
            focusInputOnSuggestionClick={Boolean(this.state.textFieldInput) ? false : true}
            inputProps={
              {
                classes,
                chips: this.state.value,
                onChange: this.handletextFieldInputChange,
                value: this.state.textFieldInput,
                onAdd: (chip: string) => this.handleAddChip(chip),
                onEnter: (e: Event) => this.handleOnInputEnter(e),
                onDelete: (chip: unknown, index: number) => this.handleDeleteChip(chip, index),
              } as unknown as RenderInputProps & WithStyles<typeof styles>
            }
          />
        </Grid>
        <Grid item xs={1} className={classes.discardGrid}>
          <IconButton
            onClick={this.clear}
            disabled={!this.state.textFieldInput.length && !this.state.value.length}
            className={classes.discardButton}
            size="large"
          >
            <DiscardIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  }
  private onSuggestionSelected = (_: unknown, args: SuggestionSelectedEventData<Types.Suggestion>) => {
    const { suggestionValue } = args;
    this.justClicked = true;
    if (this.currentKey) {
      const index = findIndex(this.props.suggestions, (s: Types.Suggestion) => s.name === this.currentKey);
      const suggestionIndex = this.props.suggestions[index].options.findIndex((o) => o.name === suggestionValue);
      const value = this.props.suggestions[index].options[suggestionIndex].value;
      const chip = this.currentKey + ': ' + value;
      this.handleAddChip(chip);
    } else {
      this.currentKey = suggestionValue;
      this.setState({
        // currentKey: suggestionValue,
        textFieldInput: suggestionValue + ': ',
      });
      setTimeout(() => {
        this.handleSuggestionsFetchRequested({ value: suggestionValue + ': ' });
      }, 10);
    }
  };
}
const enhance = compose<unknown, Properties>(withStyles(styles, { withTheme: true }));
export default enhance(AutosuggestChipInput as React.ComponentType<unknown> as React.ComponentType<unknown>);
